import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useStoreSettings } from '../../hooks/useStoreSettings';
import ProductCard from '../ProductCard';
import styles from './FeaturedProducts.module.css';

interface Product {
  id: string;
  name: string;
  description: string;
  short_description: string;
  price: number;
  sale_price: number | null;
  stock_quantity: number;
  image_url: string;
  is_active: boolean;
  category_id: string;
  slug: string;
  old_price?: number;
}

interface FeaturedProductsProps {
  limit?: number;
  showDescriptions?: boolean;
}

const FeaturedProducts: React.FC<FeaturedProductsProps> = ({ limit = 8, showDescriptions = true }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { formatPrice } = useStoreSettings();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Jednostavniji pristup bez JOIN-a
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('is_active', true)
          .eq('is_featured', true)
          .order('name', { ascending: true })
          .limit(limit);

        if (error) throw error;

        // Ako imamo proizvode, dohvati vidljive kategorije pa filtriraj
        if (data && data.length > 0) {
          const { data: categoriesData, error: categoriesError } = await supabase
            .from('categories')
            .select('id, is_visible')
            .eq('is_visible', true);
            
          if (categoriesError) throw categoriesError;
          
          // Filtriraj proizvode da budu samo oni iz vidljivih kategorija
          const visibleCategoryIds = categoriesData.map(cat => cat.id);
          const filteredProducts = data.filter(product => 
            visibleCategoryIds.includes(product.category_id)
          );
          
          console.log('Fetched featured products:', filteredProducts);
          setProducts(filteredProducts || []);
        } else {
          setProducts([]);
        }
      } catch (err: any) {
        console.error('Error fetching featured products:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [limit]);

  if (loading) return <div className={styles.loading}>Učitavanje proizvoda...</div>;
  if (error) return <div className={styles.error}>Greška: {error}</div>;
  if (products.length === 0) return <div className={styles.noProducts}>Trenutno nema istaknutih proizvoda.</div>;

  return (
    <div className={styles.productsGrid}>
      {products.map((product, idx) => (
        <ProductCard key={product.id} product={product} index={idx} showDescription={showDescriptions} />
      ))}
    </div>
  );
};

export default FeaturedProducts; 